import React, { useEffect, useState } from "react";
import { connect } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import LoadingDialog from "../../components/Common/LoadingDialog";
import Notification from "../../components/Common/Notification";
import { ButtonBase, Grid } from "@mui/material";
import { Icon } from "@iconify/react/dist/iconify.js";
import Helpers from "../../helpers/helpers";
import { exportAccessoriesMasterApi } from "../../store/product/productApi";
import "./setting.css"


const Settings = ({ userPermission }) => {
    const navigate = useNavigate();

    const [loading, setIsLoading] = useState(false);
    const [canExportProduct, setCanExportProduct] = useState(false);

    const [notification, setNotification] = useState({
        open: false,
        msg: "",
        type: "success",
    });

    useEffect(() => {
        if (userPermission?.length > 0) {
            setCanExportProduct(userPermission.includes("export-product"));
        }
    }, [userPermission]);

    const renderHeader = () => {
        return (
            <div className="d-flex justify-content-between align-items-center p-1 pb-2">
                <div className="header">Settings</div>
            </div>
        )
    }

    const settingCategoryButton = (iconName, title, subtitle, navigateTo, onClick = null) => {
        return (
            <Grid item lg={4} md={6} sm={12} display={'flex'}>
                <ButtonBase
                    style={{ border: '1px solid #EDEDED', minHeight: 99, width: '100%' }}
                    onClick={() => {
                        if (onClick) {
                            onClick();
                            return;
                        }
                        navigate(navigateTo);
                    }}
                >
                    <Grid container className="d-flex align-self-center p-3 px-2" textAlign={'left'} spacing={2}>
                        <Grid item lg={2} md={2.3} textAlign={'center'}>
                            <Icon icon={iconName} fontSize={50} style={{ color: '#D0D0D0' }} />
                        </Grid>
                        <Grid item lg={10} md={9.7}>
                            <div className="setting-header">{title}</div>
                            <div className="setting-subheader">{subtitle}</div>
                        </Grid>
                    </Grid>
                </ButtonBase>
            </Grid>
        )
    }

    const downloadMasterCopy = async () => {
        setIsLoading(true);
        try {
            const response = await exportAccessoriesMasterApi();

            Helpers.handleDownloadExcelFileExact(response.excel_base64, response.filename);
            setNotification({
                open: true,
                msg: 'Accessories master copy downloaded.',
                type: 'success',
            });
        } catch (error) {
            setNotification({
                open: true,
                msg: typeof error === 'string' ? error : 'Failed to download master copy.',
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    }

    const renderSystemSetting = () => {
        return (
            <>
                <div className="setting-header">System</div>
                <Grid container marginTop={1} spacing={2}>
                    {settingCategoryButton('solar:settings-bold-duotone', 'General', 'Control the system settings of the platform.', '/settings-general')}
                    {settingCategoryButton('solar:palette-bold-duotone', 'Appearance', 'Manage homepage section background images for desktop and mobile.', '/settings-appearance')}
                    {settingCategoryButton('solar:shield-check-bold-duotone', 'Homepage Policy', 'Edit the green policy card on the homepage. Trust bar stays fixed.', '/settings-homepage-policy')}
                    {settingCategoryButton('solar:document-text-bold-duotone', 'Category Pages', 'Create and edit content pages with custom URLs and SEO fields.', '/settings-content-pages')}
                    {settingCategoryButton('solar:hamburger-menu-bold-duotone', 'Menu', 'Edit header navigation links. SHOP mega-menu stays fixed.', '/settings-navigation-menu')}
                    {settingCategoryButton('solar:user-id-bold-duotone', 'Users', 'Manage the list of admin users with access to the backend system.', '/settings-users')}
                    {settingCategoryButton('solar:tuning-square-bold-duotone', 'Roles', 'Define roles with specific permissions for accessing different modules.', '/settings-roles')}
                </Grid>
            </>
        )
    }
    const renderProductSetting = () => {
        return (
            <div className="mt-4">
                <div className="setting-header">Product</div>
                <Grid container marginTop={1} spacing={2}>
                    {settingCategoryButton('solar:widget-5-bold-duotone', 'Categories', 'Set up and manage product categories for better organization.', '/settings-product-categories')}
                    {settingCategoryButton('solar:garage-bold-duotone', 'Manufacturers', 'Manage the list of manufacturers for product association.', '/settings-product-manufacturers')}
                    {settingCategoryButton('solar:laptop-minimalistic-bold-duotone', 'Models', 'Define specific product models under the corresponding categories.', '/settings-product-models')}
                    {settingCategoryButton('solar:cpu-bold-duotone', 'Configurations', 'Set up detailed configurations that can be selected when creating products.', '/settings-product-configurations')}
                    {settingCategoryButton('solar:like-bold-duotone', 'Services', 'Configure additional services that can be applied to a product.', '/settings-product-services')}
                    {settingCategoryButton('solar:buildings-3-bold', 'Schools', 'Manage the list of schools under specific contracts.', '/settings-school')}
                </Grid>
            </div>
        )
    }

    const renderShippingSetting = () => {
        return (
            <div className="mt-4">
                <div className="setting-header">Shipping</div>
                <Grid container marginTop={1} spacing={2}>
                    {settingCategoryButton('solar:scooter-bold-duotone', 'Shipping Methods', 'Define available shipping methods for customers to choose at checkout', '/settings-shipping-methods')}
                    {settingCategoryButton('solar:wad-of-money-bold-duotone', 'Shipping Rates', 'Set rates that may vary by order amount or weight.', '/settings-shipping-rates')}
                </Grid>
            </div>
        )
    }

    const renderPaymentSetting = () => {
        return (
            <div className="mt-4">
                <div className="setting-header">Payment</div>
                <Grid container marginTop={1} spacing={2}>
                    {settingCategoryButton('solar:wallet-bold-duotone', 'Payment Methods', 'Configure and manage available payment options on the platform.', '/settings-payment-methods')}
                </Grid>
            </div>
        )
    }

    const renderMasterCopySetting = () => {
        if (!canExportProduct) {
            return null;
        }

        return (
            <div className="my-4">
                <div className="setting-header">Master Copy</div>
                <Grid container marginTop={1} spacing={2}>
                    {settingCategoryButton(
                        'solar:file-download-bold-duotone',
                        'Download all accessories',
                        'Latest accessories catalog not linked to a laptop bundle. Edit and re-upload via Accessories import.',
                        null,
                        () => downloadMasterCopy()
                    )}
                </Grid>
            </div>
        )
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <LoadingDialog open={loading} />
                <Container fluid>
                    <Notification notification={notification} setNotification={setNotification} />
                    {renderHeader()}
                    <Card>
                        <CardBody>
                            {renderSystemSetting()}
                            {renderProductSetting()}
                            {renderShippingSetting()}
                            {renderPaymentSetting()}
                            {renderMasterCopySetting()}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

function mapStateToProps({ Login }) {
 return({
    userPermission: Login?.permissionArry,
 })
}


export default connect(mapStateToProps)(Settings);
